import { defineStore } from 'pinia'
import axios from 'axios'
import { api, unAuthApi } from 'boot/receiver/axios'
import { i18n } from 'boot/common/i18n'
import { Cookies } from 'quasar'

import { useErrorStore } from 'stores/common/error'
import { useUserStore } from 'stores/receiver/account/user/user'
import { useTwoFactorStore } from 'stores/receiver/account/security/twoFactor'

import checkSudoCode from 'composables/receiver/auth/checkSudoCode'
import { useCookieHelpers } from 'composables/common/auth/useCookieHelpers'
import { useCommonAuthStore } from 'stores/common/rulesEditor/commonAuth'

import type {
  ActiveChallenges,
  CreateTokenWithcode202Res,
  GetSudoRes,
  PreAuthWithCredentialsRes,
  ConnectRes,
  SignInWithGoogleRes,
  SignUpWithCredentialsParams,
  VerifyMailRes,
} from 'types/receiver/account/auth'
import type { PromiseReturn, SuccessApiRes } from 'types/common/api'
import type { User } from 'types/receiver/account/user'
import type { TwoFactorConfig } from 'types/common/account/twoFactor'
import type { AuthTokens, MiddleWareAuthResponse } from 'types/common/auth/auth'
import { type QSsrContext } from '#q-app'
import type { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    // login
    sudo_code: null as null | string,
    challenges: null as null | ActiveChallenges,
  }),

  getters: {
    activeChallenges(state) {
      if (state.challenges) {
        const activeChallenges = Object.fromEntries(
          Object.entries(state.challenges).filter(
            (val) => typeof val[1] === 'boolean' && val[1]
          )
        )
        return Object.keys(activeChallenges)
      } else return []
    },
  },

  actions: {
    // SIGN UP
    async verifyMail(
      code: string,
      digits_identifier: string
    ): PromiseReturn<VerifyMailRes> {
      try {
        const res = await unAuthApi.post('/account/registration/verify', {
          digits_identifier,
          code,
        })
        const data: VerifyMailRes = res?.data
        return data
      } catch (error) {
        return { success: false, error }
      }
    },

    // SIGN IN

    // PROVIDERS
    async signInWithGoogle(redirectUri: string) {
      useCookieHelpers().removeAccessToken()
      try {
        const res = await unAuthApi.post('/account/google/connect', {
          redirect_uri: redirectUri,
        })

        const data: SignInWithGoogleRes = res?.data

        if (data && data.success) {
          return data.payload.authorization_uri
        } else {
          useErrorStore().backErrors.providerLogin = i18n.global.t(
            'account.signIn.providerFail'
          )
        }
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    // CREDENTIALS

    async preAuthWithCredentials({
      email,
      recaptcha_result,
      successful_signup_url,
      timezone,
    }: SignUpWithCredentialsParams) {
      try {
        const res = await unAuthApi.post('/account/connect', {
          email,
          recaptcha_result,
          successful_signup_url,
          timezone,
        })
        if (res.status === 200) {
          return { payload: res.data.payload, status: res.status } as {
            payload: PreAuthWithCredentialsRes
            status: 200
          }
        } else if (res.status === 201) {
          return { payload: res.data.payload, status: res.status } as {
            payload: { digits_identifier: string }
            status: 201
          }
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async signInWithPassword(
      password: string,
      pre_authentication_code: string
    ): Promise<
      | { success: true; authentication_code: string }
      | { success: false; status: number }
    > {
      try {
        const res = await unAuthApi.post('/account/connect/password', {
          pre_authentication_code,
          password,
        })

        const data: ConnectRes = res.data
        if (data.success) {
          return {
            success: true,
            authentication_code: data.payload.authentication_code,
          }
        } else return { success: false, status: 400 }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          useErrorStore().updateBackErrors(error)

          return { success: false, status: error.response?.status as number }
        } else return { success: false, status: 400 }
      }
    },

    async signInWithEmail(
      pre_authentication_code: string,
      code: string
    ): PromiseReturn<{ success: true; authentication_code: string }> {
      try {
        const res = await unAuthApi.post('/account/connect/email', {
          pre_authentication_code,
          code,
        })

        const data: ConnectRes = res.data
        if (data.success) {
          return {
            success: true,
            authentication_code: data.payload.authentication_code,
          }
        } else return { success: false, error: { status: 400 } }
      } catch (error) {
        return { success: false, error }
      }
    },

    // TOKENS
    async createTokens(
      code: string
    ): PromiseReturn<{ success: true; status: number }> {
      try {
        const res = await api.post(
          '/account/access-token/authentication-code',
          {
            authentication_code: code,
          }
        )
        const data: MiddleWareAuthResponse | CreateTokenWithcode202Res =
          res.data

        if (data.success) {
          if (res.status === 200) {
            const payload = data.payload as AuthTokens
            const cookieHelpers = useCookieHelpers()
            cookieHelpers.setAccessToken(payload.access_token)
            cookieHelpers.setRefreshToken(payload.refresh_token)
          } else if (res.status === 202) {
            const payload = data.payload as ActiveChallenges
            this.challenges = payload
          }
          return { success: true, status: res.status }
        } else return { success: false, error: '' }
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return { success: false, error }
      }
    },

    async refreshTokens(ssrContext?: QSsrContext | null) {
      const cookieHelpers = useCookieHelpers()
      try {
        const cookies = process.env.SERVER
          ? Cookies.parseSSR(ssrContext)
          : Cookies // otherwise we're on client

        const res = await api.post(
          '/account/access-token/refresh-token',
          {
            refresh_token: cookies.get('refresh_token'),
          },
          {
            skipAuthRefresh: true, // Skip auth refresh interceptor
          } as AxiosAuthRefreshRequestConfig
        )

        const data: MiddleWareAuthResponse = res.data
        if (data.success) {
          cookieHelpers.setAccessToken(data.payload.access_token, ssrContext)
          cookieHelpers.setRefreshToken(data.payload.refresh_token, ssrContext)

          return data
        } else {
          cookieHelpers.removeAccessToken(ssrContext)
          cookieHelpers.removeRefreshToken(ssrContext)
          return false
        }
      } catch (error) {
        cookieHelpers.removeAccessToken(ssrContext)
        cookieHelpers.removeRefreshToken(ssrContext)
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    // First auth
    async updateFirstAuth(
      preferred_authentication_method: User['preferred_authentication_method'],
      sudo_code: string
    ) {
      try {
        const res = await api.put('/account/update/authentication-method', {
          sudo_code,
          preferred_authentication_method,
        })

        const data: SuccessApiRes<{
          user: User
          two_factor_status: TwoFactorConfig
        }> = res.data
        if (data.success) {
          useUserStore().user = data.payload.user
          useTwoFactorStore().twoFactorConfig = data.payload.two_factor_status
          return true
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        checkSudoCode(error)
        return false
      }
    },

    // SUDO
    // Password
    async getSudoWithPassword(
      current_password: string,
      recaptcha_result: string
    ) {
      try {
        const res = await api.post('/account/sudo/password', {
          current_password,
          recaptcha_result,
        })

        const data: GetSudoRes = res.data

        if (data && data.success) {
          this.sudo_code = data.payload.sudo_code
          return true
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    // Email
    async emailChallengeSudo(recaptcha_result: string) {
      try {
        const res = await api.post('/account/sudo/email/challenge', {
          recaptcha_result,
        })

        if (res.status === 202) {
          return true
        } else return false
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        return false
      }
    },

    async checkEmailChallengeSudo(
      code: string
    ): PromiseReturn<{ success: true }> {
      try {
        const res = await api.post('/account/sudo/email/pass', {
          code,
        })

        const data = res.data

        if (data.success) {
          this.sudo_code = data.payload.sudo_code
          return { success: true }
        } else return { success: false, error: { status: 400 } }
      } catch (error) {
        return { success: false, error }
      }
    },

    async signOut() {
      const { logoutClean } = useCommonAuthStore()

      try {
        const res = await api.post('/account/access-token/revoke', {
          refresh_token: Cookies.get('refresh_token'),
        })

        if (res.status >= 200 && res.status < 300) {
          const cookieHelpers = useCookieHelpers()
          cookieHelpers.removeRefreshToken()
          cookieHelpers.removeAccessToken()
          logoutClean()
        }
      } catch (error) {
        useErrorStore().updateBackErrors(error)
        logoutClean()
      }
    },
  },
})
